import { useEffect, useState } from 'react';
import { Users, StickyNote } from 'lucide-react';
import { STAVY, cas, naDatum } from './rezervaceUi';

/** „18:30" → 1110 minut od půlnoci. */
const naMinuty = (hhmm) => {
    const [h, m] = String(hhmm ?? '').split(':').map(Number);
    return Number.isFinite(h) ? h * 60 + (m || 0) : 0;
};

/**
 * Minuty od půlnoci **zobrazovaného dne** — může vyjít i přes 1440 nebo do minusu.
 * Z `getHours()` to počítat nešlo: rezervace do 01:00 vyšla jako 60 minut, tedy
 * před svým začátkem, a blok se zahodil jako nulový.
 */
const minutOdPulnoci = (iso, datum) =>
    Math.round((new Date(iso) - new Date(`${datum}T00:00:00`)) / 60000);

const dolu = (min) => Math.floor(min / 60) * 60;
const nahoru = (min) => Math.ceil(min / 60) * 60;

const DEN = 24 * 60;
/** Kam nejdál smí osa přetéct přes půlnoc — do dvou ráno stačí i na silvestra. */
const KONEC_MAX = DEN + 120;

/**
 * Časová osa dne — řádek je místo, sloupec čas. Stejný pohled jako v pokladně:
 * o obsazenosti vypovídá prázdno mezi bloky, ne seznam jmen. U dneška svislá
 * čára „teď".
 */
export default function OsaDne({ sekce, rezervace, provoz, datum, borderColor, mutedColor, vybranyId = null, onOtevrit }) {
    const [ted, setTed] = useState(() => new Date());
    const jeDnes = datum === naDatum(ted);
    useEffect(() => {
        if (!jeDnes) return undefined;
        const t = setInterval(() => setTed(new Date()), 60000);
        return () => clearInterval(t);
    }, [jeDnes]);

    const dleMista = new Map();
    const viditelne = [];
    for (const r of rezervace) {
        if (r.stav === 'zrusena') continue;
        const klice = r.cela_sekce ? [`s${r.zona?.id}`] : (r.stoly || []).map(s => `t${s.id}`);
        if (!klice.length) continue;
        viditelne.push(r);
        for (const k of klice) {
            if (!dleMista.has(k)) dleMista.set(k, []);
            dleMista.get(k).push(r);
        }
    }

    // Osa vychází z provozní doby, ale roztáhne se na to, co ten den opravdu je —
    // rezervace mimo otevírací dobu musí být vidět, ne zmizet. Vícedenní akce
    // osu netahá: do dne jen zasahuje a nakreslí se od kraje ke kraji.
    let zacatekOsy = naMinuty(provoz?.od ?? '10:00');
    let konecOsy = Math.max(naMinuty(provoz?.do ?? '22:00'), zacatekOsy + 60);
    for (const r of viditelne) {
        const od = minutOdPulnoci(r.zacatek, datum);
        const doK = minutOdPulnoci(r.konec, datum);
        if (od >= 0 && od < DEN) zacatekOsy = Math.min(zacatekOsy, dolu(od));
        if (doK > 0) konecOsy = Math.max(konecOsy, Math.min(nahoru(doK), KONEC_MAX));
    }
    zacatekOsy = Math.max(0, zacatekOsy);

    const rozsah = konecOsy - zacatekOsy;
    const hodin = Math.max(1, Math.round(rozsah / 60));
    const tedMin = jeDnes ? ted.getHours() * 60 + ted.getMinutes() : null;
    const tedVidet = tedMin != null && tedMin >= zacatekOsy && tedMin <= konecOsy;

    const radky = [];
    for (const s of sekce || []) {
        radky.push({ klic: `s${s.id}`, nazev: s.nazev, sekce: true, kapacita: s.kapacita, barva: s.barva });
        for (const stul of s.stoly || []) {
            radky.push({ klic: `t${stul.id}`, nazev: stul.nazev || `Stůl ${stul.cislo}`, kapacita: stul.kapacita });
        }
    }

    if (!radky.length) {
        return <p className="text-sm text-center" style={{ color: mutedColor, padding: '40px 16px' }}>Zatím není žádná sekce ani stůl — půdorys se zakládá v pokladně.</p>;
    }

    const POPISKY = 170;
    return (
        <div style={{ overflowX: 'auto', padding: '12px 14px 16px' }}>
            <div style={{ minWidth: 720, position: 'relative' }}>
                <div className="flex" style={{ height: 20 }}>
                    <div style={{ width: POPISKY, flexShrink: 0 }} />
                    <div className="relative flex-1">
                        {Array.from({ length: hodin + 1 }, (_, i) => (
                            <div key={i} className="absolute top-0 text-[10px] font-semibold"
                                style={{
                                    left: `${(i / hodin) * 100}%`,
                                    transform: i === hodin ? 'translateX(-100%)' : 'none',
                                    color: mutedColor, fontVariantNumeric: 'tabular-nums',
                                }}>
                                {String(Math.floor((zacatekOsy + i * 60) / 60) % 24).padStart(2, '0')}
                            </div>
                        ))}
                    </div>
                </div>

                {radky.map(radek => (
                    <div key={radek.klic} className="flex items-stretch" style={{ height: radek.sekce ? 34 : 32 }}>
                        <div className="flex items-center gap-1.5 pr-2 overflow-hidden" style={{ width: POPISKY, flexShrink: 0 }}>
                            {radek.sekce && (
                                <span style={{ width: 8, height: 8, borderRadius: 2, background: radek.barva || mutedColor, flexShrink: 0 }} />
                            )}
                            <span className="truncate"
                                style={{
                                    fontSize: radek.sekce ? 12 : 11.5,
                                    fontWeight: radek.sekce ? 700 : 500,
                                    paddingLeft: radek.sekce ? 0 : 14,
                                    color: radek.sekce ? 'var(--text-primary)' : 'var(--text-secondary)',
                                }}>
                                {radek.nazev}
                            </span>
                            {radek.kapacita > 0 && (
                                <span className="text-[10px]" style={{ color: mutedColor, flexShrink: 0 }}>{radek.kapacita}</span>
                            )}
                        </div>

                        <div className="relative flex-1"
                            style={{
                                borderTop: `1px solid ${borderColor}`,
                                backgroundImage: `linear-gradient(to right, ${borderColor} 1px, transparent 1px)`,
                                backgroundSize: `${100 / hodin}% 100%`,
                            }}>
                            {(dleMista.get(radek.klic) || []).map(r => {
                                const zac = Math.max(minutOdPulnoci(r.zacatek, datum), zacatekOsy);
                                const kon = Math.min(minutOdPulnoci(r.konec, datum), konecOsy);
                                if (kon <= zac) return null;
                                const barva = (STAVY[r.stav] || STAVY.potvrzena).color;
                                const vybrany = r.id === vybranyId;

                                return (
                                    <button key={r.id} type="button" onClick={() => onOtevrit(r)}
                                        title={`${cas(r.zacatek)}–${cas(r.konec)} · ${r.jmeno} · ${r.pocet_hostu} hostů`}
                                        className="absolute flex items-center gap-1 px-1.5 overflow-hidden"
                                        style={{
                                            left: `${((zac - zacatekOsy) / rozsah) * 100}%`,
                                            width: `${((kon - zac) / rozsah) * 100}%`,
                                            top: 3, bottom: 3, minWidth: 26, borderRadius: 5,
                                            background: `${barva}33`, border: `1px solid ${barva}88`,
                                            boxShadow: vybrany ? `0 0 0 2px ${barva}` : 'none',
                                            color: barva, cursor: 'pointer',
                                        }}>
                                        <span className="text-[11px] font-semibold truncate">{r.jmeno}</span>
                                        <span className="flex items-center gap-0.5 text-[10px]" style={{ flexShrink: 0 }}>
                                            <Users size={9} />{r.pocet_hostu}
                                        </span>
                                        {r.poznamka && <StickyNote size={9} style={{ flexShrink: 0 }} />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}

                {tedVidet && (
                    <div aria-hidden="true" style={{
                        position: 'absolute', top: 16, bottom: 0, width: 2, background: '#EF4444', pointerEvents: 'none',
                        left: `calc(${POPISKY}px + (100% - ${POPISKY}px) * ${(tedMin - zacatekOsy) / rozsah})`,
                    }} />
                )}
            </div>
        </div>
    );
}
